import { ossConsolePath } from '@logto/schemas';
import { Suspense, useContext } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { safeLazy } from 'react-safe-lazy';
import { SWRConfig } from 'swr';

import AppLoading from '@/components/AppLoading';
import RedirectToAccountCenter from '@/components/RedirectToAccountCenter';
import { isCloud, isDevFeaturesEnabled, isMultiTenant, isProduction } from '@/consts/env';
import AppBoundary from '@/containers/AppBoundary';
import AppContent, { RedirectToFirstItem } from '@/containers/AppContent';
import ConsoleContent from '@/containers/ConsoleContent';
import OssOnboardingGuard from '@/containers/OssOnboardingGuard';
import ProtectedRoutes from '@/containers/ProtectedRoutes';
import TenantAccess from '@/containers/TenantAccess';
import { TenantsContext, GlobalRoute } from '@/contexts/TenantsProvider';
import useSwrOptions from '@/hooks/use-swr-options';
import Callback from '@/pages/Callback';
import CheckoutSuccessCallback from '@/pages/CheckoutSuccessCallback';
import { dropLeadingSlash } from '@/utils/url';

import { __Internal__ImportError } from './internal';

const Welcome = safeLazy(async () => import('@/pages/Welcome'));
const OssOnboarding = safeLazy(async () => import('@/pages/OssOnboarding'));
const CreateFirstTenant = safeLazy(async () => import('@/pages/CreateFirstTenant'));

/**
 * Root-redirect for self-hosted multi-tenant OSS. Decides what to render at `/`:
 *
 * - If the tenant list is still loading, show `<AppLoading />`.
 * - If the user has zero tenants, render the onboarding `<CreateFirstTenant />` page.
 * - Otherwise navigate to the first tenant (`/${tenants[0].id}`).
 *
 * The tenant list is populated by `Content` in `App.tsx` once auth completes, so by the time
 * a signed-in user reaches this component the list is guaranteed to be resolved.
 */
function MultiTenantRootRedirect() {
  const { isInitComplete, tenants } = useContext(TenantsContext);

  if (!isInitComplete) {
    return <AppLoading />;
  }

  const [firstTenant] = tenants;
  if (!firstTenant) {
    return <CreateFirstTenant />;
  }

  return <Navigate replace to={`/${firstTenant.id}`} />;
}

function Layout() {
  const swrOptions = useSwrOptions();

  return (
    <SWRConfig value={swrOptions}>
      <AppBoundary>
        <Outlet />
      </AppBoundary>
    </SWRConfig>
  );
}

export function ConsoleRoutes() {
  return (
    <Suspense fallback={<AppLoading />}>
      <Routes>
        {/**
         * OSS doesn't have a tenant concept nor root path handling component, but it may
         * navigate to the root path in frontend. In this case, we redirect it to the OSS
         * console path to trigger the console routes.
         */}
        {!isCloud && !isMultiTenant && (
          <Route path="/" element={<Navigate to={ossConsolePath} />} />
        )}
        {!isCloud && isMultiTenant && (
          <>
            {/**
             * The registered redirect URI is `{ADMIN_ENDPOINT}/console/callback`. With the
             * React Router basename set to `/console` in multi-tenant OSS the incoming path
             * is just `/callback` (one segment), so it won't match `:tenantId/callback`.
             * Mount a bare `/callback` route here so `useHandleSignInCallback` can finish the
             * token exchange before anything else redirects.
             */}
            <Route path="callback" element={<Callback />} />
            {/**
             * Gate the root redirect behind `ProtectedRoutes`. Without this, an unauthenticated
             * visitor at `/console` sees `AppLoading` forever: `isInitComplete` can't flip
             * true until `isAuthenticated` does, but nothing else on `/` ever triggers sign-in.
             */}
            <Route element={<ProtectedRoutes />}>
              <Route path="/" element={<MultiTenantRootRedirect />} />
            </Route>
          </>
        )}
        <Route path="/:tenantId" element={<Layout />}>
          <Route path="callback" element={<Callback />} />
          <Route path="welcome" element={<Welcome />} />
          {isDevFeaturesEnabled && (
            <Route path="__internal__/import-error" element={<__Internal__ImportError />} />
          )}
          <Route element={<ProtectedRoutes />}>
            <Route
              path={dropLeadingSlash(GlobalRoute.Profile) + '/*'}
              element={<RedirectToAccountCenter />}
            />
            <Route element={<TenantAccess />}>
              {!isCloud && isProduction && isDevFeaturesEnabled && (
                <Route path="onboarding" element={<OssOnboarding />} />
              )}
              {isCloud && (
                <Route
                  path={dropLeadingSlash(GlobalRoute.CheckoutSuccessCallback)}
                  element={<CheckoutSuccessCallback />}
                />
              )}
              <Route element={<OssOnboardingGuard />}>
                <Route element={<AppContent />}>
                  <Route index element={<RedirectToFirstItem />} />
                  <Route path="*" element={<ConsoleContent />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
